import { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { SlidersHorizontal, X } from "lucide-react";
import { useTeam } from "@/hooks/useTeam";
import { useContactFilters } from "@/hooks/useContactFilters";

export interface ChatFilterValues {
  attributionId: string | null;
  statusChat: string | null;
  tag: string | null;
}

interface ChatFiltersProps {
  filters: ChatFilterValues;
  onFiltersChange: (filters: ChatFilterValues) => void;
}

const defaultFilters: ChatFilterValues = {
  attributionId: null,
  statusChat: null,
  tag: null,
};

export const ChatFilters = ({ filters, onFiltersChange }: ChatFiltersProps) => {
  const { members, loading: loadingMembers } = useTeam();
  const { uniqueTags, loading: loadingTags } = useContactFilters();
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<ChatFilterValues>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const activeFiltersCount = [
    filters.attributionId,
    filters.statusChat,
    filters.tag,
  ].filter(Boolean).length;

  const handleApply = () => {
    onFiltersChange(localFilters);
    setIsOpen(false);
  };

  const handleClear = () => {
    setLocalFilters(defaultFilters);
    onFiltersChange(defaultFilters);
    setIsOpen(false);
  };

  const handleSelectChange = (key: keyof ChatFilterValues, value: string) => {
    const newValue = value === "__all__" ? null : value;
    setLocalFilters(prev => ({ ...prev, [key]: newValue }));
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-[40px] px-3 border border-neutral-500 bg-neutral-600 hover:bg-neutral-500 transition-colors rounded-[6px] flex items-center justify-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-neutral-300" />
          {activeFiltersCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-blue-600 hover:bg-blue-600 text-white text-xs">
              {activeFiltersCount}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] bg-neutral-800 border-neutral-700 p-4" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-neutral-100">Filtros</span>
            {activeFiltersCount > 0 && (
              <button
                onClick={handleClear}
                className="text-xs text-neutral-400 hover:text-neutral-200 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Limpar
              </button>
            )}
          </div>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs text-neutral-400">Status</label>
              <Select
                value={localFilters.statusChat || "__all__"}
                onValueChange={(v) => handleSelectChange("statusChat", v)}
              >
                <SelectTrigger className="h-9 bg-neutral-700 border-neutral-600 text-sm">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="__all__">Todos</SelectItem>
                    <SelectItem value="aberto">Aberto</SelectItem>
                    <SelectItem value="concluido">Concluido</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-neutral-400">Atribuido para</label>
              <Select
                value={localFilters.attributionId || "__all__"}
                onValueChange={(v) => handleSelectChange("attributionId", v)}
              >
                <SelectTrigger className="h-9 bg-neutral-700 border-neutral-600 text-sm">
                  <SelectValue placeholder={loadingMembers ? "Carregando..." : "Todos"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="__all__">Todos</SelectItem>
                    <SelectItem value="__unassigned__">Não atribuído</SelectItem>
                    {members
                      .filter((m) => m.auth_user_id)
                      .map((member) => (
                        <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                          {member.name || member.email}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-neutral-400">Tag</label>
              <Select
                value={localFilters.tag || "__all__"}
                onValueChange={(v) => handleSelectChange("tag", v)}
              >
                <SelectTrigger className="h-9 bg-neutral-700 border-neutral-600 text-sm">
                  <SelectValue placeholder={loadingTags ? "Carregando..." : "Todas"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {uniqueTags.map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        {tag}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={handleApply} className="w-full h-9 text-sm">
            Aplicar filtros
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { defaultFilters as defaultChatFilters };
